<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="`(As of ${asOfDate})`"
        />
        <b-card>
            <div id="report-body" class="w-100">
                <div class="mt-3" v-if="hasReopenedInvoices">
                    <ArInvoiceNote show @refresh="refresh" />
                </div>

                <QTable
                    :columns="columns"
                    :data="tableData"
                    hide-bottom
                    :loading="isLoading"
                    class="mb-4"
                    :rows-per-page-options="[0]"
                    row-key="desc"
                >
                    <template v-slot:header-cell-checkbox>
                        <th style="vertical-align: middle;">
                            <input
                                type="checkbox"
                                style="cursor: pointer;"
                                @change="toggleAll"
                            />
                        </th>
                    </template>

                    <template v-slot:body-cell-checkbox="props">
                        <td>
                            <input
                                type="checkbox"
                                style="cursor: pointer;"
                                v-model="checked"
                                :value="props.row.client_id"
                            />
                        </td>
                    </template>
                    <template v-slot:body-cell-first_name="props">
                        <td>
                            <a
                                v-if="props.row.active"
                                :href="
                                    '/view/client/profile/' +
                                    props.row.encoded_id
                                "
                            >
                                {{ props.value }}
                            </a>
                            <span v-else>{{ props.value }} (deleted)</span>
                        </td>
                    </template>
                    <template v-slot:body-cell-last_name="props">
                        <td>
                            <a
                                v-if="props.row.active"
                                :href="
                                    '/view/client/profile/' +
                                    props.row.encoded_id
                                "
                            >
                                {{ props.value }}
                            </a>
                            <span v-else>{{ props.value }} (deleted)</span>
                        </td>
                    </template>
                    <template v-slot:body-cell-total_due="props">
                        <td style="text-align: center;">
                            <b>{{ props.value }}</b>
                        </td>
                    </template>

                    <template v-slot:bottom-row>
                        <q-tr v-if="isNoRecords">
                            <q-td colspan="9">No records available</q-td>
                        </q-tr>
                        <q-tr v-else-if="errorMessage">
                            <q-td colspan="9">{{ errorMessage }}</q-td>
                        </q-tr>
                        <q-tr v-else class="totalRow">
                            <q-td />
                            <q-td colspan="2" style="text-align: left;">
                                TOTAL
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow.current) }}</b>
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow["1-30_days"]) }}</b>
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow["31-60_days"]) }}</b>
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow["61-90_days"]) }}</b>
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow[">90_days"]) }}</b>
                            </q-td>
                            <q-td>
                                <b>{{ _toCurrency(totalRow.total_due) }}</b>
                            </q-td>
                        </q-tr>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";
import moment from "moment";
import ArInvoiceNote from "@/components/elements/ar-note";
import CREPORT from "@/constants/report";

export default {
    components: {
        QTable,
        ArInvoiceNote,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment()
                };
            }
        }
    },
    data() {
        return {
            columns: [
                {
                    name: "checkbox",
                    field: "checkbox",
                    label: "",
                    headerStyle: "width:50px"
                },
                {
                    name: "last_name",
                    field: "last_name",
                    label: "Last Name",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "first_name",
                    field: "first_name",
                    label: "First Name",
                    align: "left",
                    headerStyle: "font-weight:bold;"
                },
                {
                    name: "current",
                    field: "current",
                    label: "Current",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                },
                {
                    name: "1-30_days",
                    field: "1-30_days",
                    label: "1-30 Days",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                },
                {
                    name: "31-60_days",
                    field: "31-60_days",
                    label: "31-60 Days",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                },
                {
                    name: "61-90_days",
                    field: "61-90_days",
                    label: "61-90 Days",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                },
                {
                    name: ">90_days",
                    field: ">90_days",
                    label: "> 90 Days",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                },
                {
                    name: "total_due",
                    field: "total_due",
                    label: "Total Due",
                    align: "center",
                    headerStyle: "font-weight:bold;",
                    format: value => {
                        return this._toCurrency(value);
                    }
                }
            ],
            tableData: [],
            isLoading: true,
            isError: false,
            errorMessage: null,
            totalRow: {},
            asOfDate: null,
            hasReopenedInvoices: false,
            title: CREPORT.TYPE_NAME[CREPORT.TYPE.ACCOUNTS_RECEIVABLE]
        };
    },
    computed: {
        isNoRecords() {
            return (
                this.tableData.length === 0 && !this.isLoading && !this.isError
            );
        },
        checked: {
            get() {
                return this.$store.state.report.accountsReceivable
                    .selectedClientIds;
            },
            set(value) {
                this.$store.commit(
                    "report/accountsReceivableRegisterClientIds",
                    value
                );
            }
        }
        // asOfDate() {
        //     return this.$store.state.report.accountsReceivable.filter.asOf;
        // }
    },
    watch: {
        checkAll(value) {
            this.toggleAll(value);
        }
    },
    destroyed() {
        this.$store.commit("report/resetAccountReceivable");
    },
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.refresh);
        this.$root.$off("report-account-receivable-fetch");
    },
    async mounted() {
        let self = this;
        await self._getSetting();
        // fetch with default params
        self.fetchData({
            to: this.getEndDate("day").format(this.getDateFormat()),
            totalDue: "0",
            exemptFlag: "0"
        });

        self.data.refreshOption = true;
        window.scrollTo(0, 0);

        self.$root.$on("report-account-receivable-fetch", self.fetchData);
        self.$root.$on("report-filter-date-range:refresh", this.refresh);
        this.checkReopenedInvoices();
    },
    methods: {
        async checkReopenedInvoices() {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}billing/invoice`,
                {
                    params: {
                        page: 1,
                        perPage: 1,
                        status: 6
                    }
                }
            );

            let totalReopened = res?.data?.response?.meta?.total ?? 0;
            if (totalReopened > 0) {
                this.hasReopenedInvoices = true;
            }
        },
        refresh() {
            let {
                asOf,
                exemptFlag,
                totalDue
            } = this.$store.state.report.accountsReceivable.filter;

            this.fetchData({
                to: asOf
                    ? asOf
                    : this.getEndDate("day").format(this.getDateFormat()),
                totalDue,
                exemptFlag
            });
        },
        toggleAll(event) {
            let isChecked = event.target.checked;

            if (isChecked) {
                let arr = [];

                for (let record of this.tableData) {
                    arr.push(record.client_id);
                }

                this.checked = arr;
            } else {
                this.checked = [];
            }
        },
        async fetchData(params = {}) {
            try {
                this.isLoading = true;
                this.isError = false;
                this.errorMessage = null;
                this.asOfDate = params.to;

                let res = await this.$http.get(
                    `${process.env.BARRAMUNDI}report/reports/${CREPORT.TYPE.ACCOUNTS_RECEIVABLE}`,
                    {
                        params: {
                            to: moment
                                .tz(
                                    params.to,
                                    this.getDateFormat(),
                                    this._timeZone
                                )
                                .endOf("day")
                                .unix(),
                            total_due: params.totalDue,
                            exempt_flag: params.exemptFlag
                        }
                    }
                );

                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code !== 200
                ) {
                    throw new Error("Error: Unable to fetch records.");
                }

                let totalDueValue = Number(params.totalDue);
                let records = [];

                if (totalDueValue > 0) {
                    let raw = res.data.response.resource.results;
                    records = raw.filter(rec => rec.total_due >= totalDueValue);
                } else {
                    records = res.data.response.resource.results;
                }

                // records = this.appendTotal(records);
                this.calculateTotal(records);

                // update selected clients store
                let previousSelectedClient =
                    this.$store.state.report.accountsReceivable
                        .selectedClientIds || [];
                let currentSelectedClient = previousSelectedClient.filter(
                    previous => _.find(records, { client_id: previous })
                );

                this.$store.commit(
                    "report/accountsReceivableRegisterClientIds",
                    currentSelectedClient
                );

                this.tableData = records;
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.isError = true;
                this.errorMessage = error.message;
            }
        },
        calculateTotal(records = []) {
            let totalRow = {
                "1-30_days": 0,
                "31-60_days": 0,
                "61-90_days": 0,
                ">90_days": 0,
                current: 0,
                total_due: 0,
                client_id: null,
                first_name: null,
                last_name: "TOTAL",
                name: null,
                isTotalRow: true
            };

            for (let record of records) {
                totalRow["1-30_days"] += record["1-30_days"] || 0;
                totalRow["31-60_days"] += record["31-60_days"] || 0;
                totalRow["61-90_days"] += record["61-90_days"] || 0;
                totalRow[">90_days"] += record[">90_days"] || 0;
                totalRow.current += record.current || 0;
                totalRow.total_due += record.total_due || 0;
            }

            this.totalRow = totalRow;

            // return [...records, totalRow];
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading .period {
    font-size: 0.7em;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
.card-body {
    padding-top: 0;
    padding-bottom: 0;
}
.totalRow td {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
    text-align: center;
}
</style>
